import React, { useEffect, useMemo, useState } from "react";

import { MoreOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";

import { isFedexPickupCategory, isShippingLabelCategory } from "./utils";

import { ReactComponent as DownloadIcon } from "../../../assets/icons/Common/Download.svg";
import { ReactComponent as PrintIcon } from "../../../assets/icons/Common/Print.svg";
import { ReactComponent as ReportIcon } from "../../../assets/icons/Common/Report.svg";
import { ReactComponent as CalendarCloseIcon } from "../../../assets/images/supply-pickup/Calendar-Close-Line.svg";
import { ReactComponent as CommentIcon } from "../../../assets/images/supply-pickup/Comment.svg";
import { ReactComponent as EditOutlineIcon } from "../../../assets/images/supply-pickup/EditOutline.svg";

import "./ActionCell.css";

const ActionCell = ({
  row,
  actionMap,
  onCreateEditClick,
  onSpInstructionClick,
  onDeleteClick,
  onPrintLabelClick,
  onDownloadLabelClick,
  onViewOrderSummaryClick,
}) => {
  const [menuItems, setMenuItems] = useState([]);
  const isPickupRow = Boolean(row?.pickup_order_id);
  const isFedexPickup = isFedexPickupCategory(row?.pickup_category);
  const canShowLabelActions = isShippingLabelCategory(row?.pickup_category)
    && String(row?.status || "").toLowerCase() !== "cancelled";
  const rowStatus = row?.status;
  const canCancelFromActionMap = Boolean(actionMap?.menu?.cancel);

  const canCancelPickup = useMemo(() => {
    if (!isPickupRow) return false;
    return rowStatus === "requested" && Boolean(canCancelFromActionMap || isFedexPickup);
  }, [isPickupRow, rowStatus, canCancelFromActionMap, isFedexPickup]);

  useEffect(() => {
    const items = [];

    if (isPickupRow) {
      if (canCancelPickup) {
        items.push({
          key: "cancel",
          label: (
            <div
              className="SupplyTableActionLabel"
              onClick={() => onDeleteClick?.({ open: true, data: row })}
            >
              <CalendarCloseIcon aria-hidden />
              <span>Cancel</span>
            </div>
          ),
        });
      }
      if (canShowLabelActions) {
        items.push({
          key: "print-label",
          label: (
            <div
              className="SupplyTableActionLabel"
              onClick={() => onPrintLabelClick?.({ data: row })}
            >
              <PrintIcon aria-hidden />
              <span>Print Label</span>
            </div>
          ),
        });
        items.push({
          key: "download-label",
          label: (
            <div
              className="SupplyTableActionLabel"
              onClick={() => onDownloadLabelClick?.({ data: row })}
            >
              <DownloadIcon aria-hidden />
              <span>Download Label</span>
            </div>
          ),
        });
      }
      items.push({
        key: "view-order-summary",
        label: (
          <div
            className="SupplyTableActionLabel"
            onClick={() => onViewOrderSummaryClick?.({ data: row })}
          >
            <ReportIcon aria-hidden />
            <span>View Order Summary</span>
          </div>
        ),
      });
      setMenuItems(items);
      return;
    }

    const handleEditOnClick = () => {
      onCreateEditClick({ pageTitle: "Edit Supply Request", data: row });
    };

    if (actionMap?.menu?.cancel || isFedexPickup) {
      items.push({
        key: "cancel",
        label: (
          <div
            className="SupplyTableActionLabel"
            onClick={() => onDeleteClick({ open: true, data: row })}
          >
            <CalendarCloseIcon />
            <span>Cancel Request</span>
          </div>
        ),
      });
    }

    if (actionMap?.menu?.edit && !isFedexPickup) {
      items.push({
        key: "edit",
        label: (
          <div className="SupplyTableActionLabel" onClick={handleEditOnClick}>
            <EditOutlineIcon />
            <span>Edit Request</span>
          </div>
        ),
      });
    }
    setMenuItems(items);
  }, [
    actionMap,
    canCancelPickup,
    canShowLabelActions,
    isFedexPickup,
    isPickupRow,
    onCreateEditClick,
    onDeleteClick,
    onDownloadLabelClick,
    onPrintLabelClick,
    onViewOrderSummaryClick,
    row,
  ]);

  const showMenu = isPickupRow
    ? menuItems.length > 0
    : Boolean(
      row.status === "requested"
      && (isFedexPickup || actionMap?.menu?.cancel || actionMap?.menu?.edit),
    );
  const showComment = Boolean(!isPickupRow && actionMap?.comment && !isFedexPickup);

  return (
    <div className="SupplyTableActionCell">
      {showComment && (
        <CommentIcon onClick={() => onSpInstructionClick({ open: true, data: row })} />
      )}
      <span
        style={{
          visibility: showMenu ? "visible" : "hidden",
          width: showMenu ? "fit-content" : "20px",
        }}
      >
        {showMenu && (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: menuItems,
              className: "SupplyTableActionDropdown",
            }}
          >
            <MoreOutlined className="SupplyTableMoreIcon" />
          </Dropdown>
        )}
      </span>
    </div>
  );
};

export default ActionCell;
